import { ButtonHTMLAttributes, forwardRef, useEffect, useState } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "utils/cn";
import {
  THEME_BRAND_SURFACE_CLASSES,
  THEME_CONNECTOR_SURFACE_CLASSES,
} from "providers/appearance/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

export enum IconType {
  Arrow = "arrow",
  Cross = "cross",
  Delete = "delete",
  Archive = "archive",
  Plus = "plus",
  Refresh = "refresh",
  Edit = "edit",
  Lock = "lock",
  PaperPlane = "paperPlane",
}

export type ButtonAccentColor =
  | "blue"
  | "green"
  | "red"
  | "yellow"
  | "purple"
  | "indigo"
  | "gray"
  | "brand"
  | "emerald"
  | "teal"
  | "cyan"
  | "violet"
  | "claude";

// Categorical accents use the `hue-*` theme tokens; hue text stays on at most a 15% tint.
const EMERALD_BADGE =
  "bg-hue-emerald/10 text-hue-emerald border-hue-emerald/20 hover:bg-hue-emerald/15 hover:border-hue-emerald/30";
const VIOLET_BADGE =
  "bg-hue-violet/10 text-hue-violet border-hue-violet/20 hover:bg-hue-violet/15 hover:border-hue-violet/30";

const BADGE_COLOR_MAP: Record<ButtonAccentColor, string> = {
  blue: "bg-hue-blue/10 text-hue-blue border-hue-blue/20 hover:bg-hue-blue/15 hover:border-hue-blue/30",
  green: EMERALD_BADGE,
  red: "bg-danger/10 text-danger border-danger/20 hover:bg-danger/20 hover:border-danger/30",
  yellow:
    "bg-warning/10 text-warning border-warning/20 hover:bg-warning/20 hover:border-warning/30",
  purple: VIOLET_BADGE,
  indigo:
    "bg-hue-indigo/10 text-hue-indigo border-hue-indigo/20 hover:bg-hue-indigo/15 hover:border-hue-indigo/30",
  violet: VIOLET_BADGE,
  gray: "border-border bg-muted text-muted-foreground hover:border-border hover:bg-muted/80",
  brand: THEME_BRAND_SURFACE_CLASSES,
  emerald: EMERALD_BADGE,
  teal: "bg-hue-teal/10 text-hue-teal border-hue-teal/20 hover:bg-hue-teal/15 hover:border-hue-teal/30",
  cyan: "bg-hue-cyan/10 text-hue-cyan border-hue-cyan/20 hover:bg-hue-cyan/15 hover:border-hue-cyan/30",
  // Partner accent, not ours: Claude's colour, resolved through --connector so it
  // still follows the active appearance preset.
  claude: THEME_CONNECTOR_SURFACE_CLASSES,
};

/** Surface classes for the badge-style button (use with `buttonVariants` on `<a>` etc.). */
export function badgeColorClasses(color?: ButtonAccentColor): string {
  const key = color ?? "brand";
  return BADGE_COLOR_MAP[key] ?? BADGE_COLOR_MAP.brand;
}

/** When `color` is omitted, map legacy `variant` values to a badge accent. */
function accentFromLegacyVariant(variant: string | null | undefined): ButtonAccentColor {
  switch (variant) {
    case "destructive":
      return "red";
    case "default":
    case "ghostBrand":
    case "badge":
      return "brand";
    default:
      return "brand";
  }
}

/** Legacy ghost-style controls: flat row / toolbar look unless an explicit `color` is passed. */
const MINIMAL_CHROME_VARIANTS = new Set<string>(["ghost", "ghostSecondary", "link"]);

const SUBTLE_VARIANTS = new Set<string>(["subtle"]);

function usesMinimalChrome(
  variant: string | null | undefined,
  color: ButtonAccentColor | undefined
): boolean {
  if (color !== undefined) return false;
  return MINIMAL_CHROME_VARIANTS.has(variant ?? "");
}

const MINIMAL_CHROME_CLASSES =
  "border-transparent bg-transparent text-foreground shadow-none transition-colors hover:scale-100 hover:bg-muted focus-visible:ring-ring/40";

const SUBTLE_CLASSES =
  "border-transparent bg-muted text-muted-foreground shadow-none transition-colors hover:scale-100 hover:bg-muted/80 hover:text-foreground focus-visible:ring-ring/40";

/** Subtle filled button classes (use with `buttonVariants` on `<a>` etc.). */
export function subtleClasses(): string {
  return SUBTLE_CLASSES;
}

function getSurfaceClasses(
  variant: string | null | undefined,
  color: ButtonAccentColor | undefined
): string {
  if (usesMinimalChrome(variant, color)) {
    return MINIMAL_CHROME_CLASSES;
  }
  if (SUBTLE_VARIANTS.has(variant ?? "")) {
    return SUBTLE_CLASSES;
  }
  return badgeColorClasses(color ?? accentFromLegacyVariant(variant));
}

const buttonVariants = cva(
  "font-inter inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:w-4 [&_svg]:h-4 [&_svg]:shrink-0 border transition-all duration-300 hover:scale-105 focus-visible:ring-current/60",
  {
    variants: {
      variant: {
        badge: "",
        default: "",
        destructive: "",
        ghost: "",
        ghostSecondary: "",
        ghostBrand: "",
        subtle: "",
        link: "",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        xs: "h-7 px-2.5 text-xs",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        auto: "h-auto px-3 py-2",
      },
    },
    defaultVariants: {
      variant: "badge",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  /** When set, loading state is shown for at least this many ms to avoid flicker */
  minLoadingMs?: number;
  /** Explicit badge tint; when set, always uses badge surfaces (not minimal ghost chrome). */
  color?: ButtonAccentColor;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "badge",
      size,
      asChild = false,
      isLoading = false,
      minLoadingMs,
      disabled,
      children,
      color,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const [displayLoading, setDisplayLoading] = useState(isLoading);
    const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null);

    useEffect(() => {
      if (isLoading) {
        setDisplayLoading(true);
        setLoadingStartedAt(Date.now());
        return;
      }
      if (minLoadingMs != null && minLoadingMs > 0 && loadingStartedAt != null) {
        const elapsed = Date.now() - loadingStartedAt;
        const remaining = minLoadingMs - elapsed;
        if (remaining > 0) {
          const timeoutId = setTimeout(() => {
            setDisplayLoading(false);
            setLoadingStartedAt(null);
          }, remaining);
          return () => clearTimeout(timeoutId);
        }
      }
      setDisplayLoading(false);
      setLoadingStartedAt(null);
    }, [isLoading, minLoadingMs, loadingStartedAt]);

    const showLoading = minLoadingMs != null ? displayLoading : isLoading;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant: "badge", size }),
          getSurfaceClasses(variant, color),
          className
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {showLoading ? (
          <>
            <span className="absolute opacity-0 overflow-hidden text-ellipsis whitespace-nowrap pointer-events-none outline-none focus:ring-0 focus:shadow-none">
              Loading
            </span>
            <FontAwesomeIcon icon={faSpinner} spin size="lg" className="animate-spin" />
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
